<template>
    <app-layout title="Products">
        <page-header>
            <template #title>Products</template>
            <template #actions>
                <jet-tertiary-button type="button" :href="route('products.logs', 'all')">
                    View logs
                </jet-tertiary-button>
                <jet-tertiary-button type="button" onclick="window.open('/products/excel_export')" title="Download all products to Excel">
                    <img src="/images/icons/excel.svg" width="14" height="14" class="mx-auto"/>
                </jet-tertiary-button>
                <jet-button type="button" :href="route('products.create')" :disabled="form.processing" >
                    Add
                </jet-button>
            </template>
        </page-header>

        <div class="px-8 mt-2">
            <div class="flex justify-between">
                <form @submit.prevent="handleSubmit">
                    
                    <div class="w-44 mb-2">
                        <multiselect class="multi-custom h-7 text-base"
                            v-model="form.load_type"
                            :options="loadTypeOptions"
                            placeholder="Load Type"
                            @change="handleSubmit"
                            :classes="{singleLabel: 'w-full px-2'}"
                            :canClear="false"
                            :canDeselect="false"
                        >
                        </multiselect>
                    </div>

                    <div class="flex gap-x-2 items-center">
                        <!-- Search / Filters -->
                        <jet-input type="text" class="w-44 h-7 text-base" name="search" v-model="form.search" @input="handleSubmit" placeholder="Search..."/>

                        <div class="w-48">
                            <multiselect class="multi-custom h-7 text-base"
                                v-model="form.category_id"
                                :options="categories"
                                valueProp="id"
                                trackBy="slug"
                                label="title"
                                :searchable="true"
                                placeholder="Category"
                                @change="handleSubmit"
                            >
                                <template v-slot:option="{ option }">
                                    <span :class="{ 'ml-5' : option.parent_id != null }">{{ option.title }} </span>
                                </template>
                            </multiselect>

                        </div>

                        <div class="w-48">
                            <multiselect class="multi-custom h-7 text-base"
                                v-model="form.enabled"
                                :options="enabledOptions"
                                placeholder="All Items"
                                @change="handleSubmit"
                                :classes="{singleLabel: 'w-full px-2'}"
                            />
                        </div>

                        <div class="flex gap-x-6">
                            <div>
                                <toggle v-model="form.is_wholesale" @change="handleSubmit" />
                                <jet-label for="is_wholesale" value="Wholesale only" class="mb-0 ml-2 inline-block" />
                            </div>

                            <div>
                                <toggle v-model="form.hot_buy" @change="handleSubmit" />
                                <jet-label for="hot-buy" value="Hot Buy" class="mb-0 ml-2 inline-block" />
                            </div>

                            <div>
                                <toggle v-model="form.local" @change="handleSubmit" />
                                <jet-label for="local" value="Local" class="mb-0 ml-2 inline-block" />
                            </div>

                            <div>
                                <toggle v-model="form.special_order" @change="handleSubmit" />
                                <jet-label for="special_order" value="Wholesale Special" class="mb-0 ml-2 inline-block" />
                            </div>

                            <div>
                                <toggle v-model="form.retail_special" @change="handleSubmit" />
                                <jet-label for="retail_special" value="Retail Special" class="mb-0 ml-2 inline-block" />
                            </div>
                        </div>

                        <jet-tertiary-button type="button" class="ml-4 py-1 h-7 self-start" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>
                </form>
            </div>
        </div>

        <div class="px-8 pt-2 pb-4">
            <div class="rounded-t border border-b-0">
                <table class="table table-with-links w-full">
                    <thead>
                        <tr>
                            <th class="text-left">BC</th>
                            <th class="text-left whitespace-nowrap">Item #</th>
                            <th class="text-left whitespace-nowrap">Master #</th>
                            <th class="text-left">Product</th>
                            <th class="text-left">Description</th>
                            <th class="text-left">Category</th>
                            <th class="text-left">UofM</th>
                            <th>Hot Buy</th>
                            <th>Local</th>
                            <th>Wholesale Special</th>
                            <th>Retail Special</th>
                            <th>Active</th>
                            <th class="text-right">Qty on Hand</th>
                            <th class="text-right">Wholesale Allocated</th>
                            <th class="text-right">Retail Allocated</th>
                            <th class="text-right">Available</th>
                            <th class="text-right">Price</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="product in products.data" :key="product.id">
                            <td>
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    <img src="/images/icons/green_check.svg" v-if="BCInfoFilled(product) == 'no_error'" width="24" height="24" class="mx-auto" />
                                    <img src="/images/icons/x-red.svg" v-if="BCInfoFilled(product) == 'error'" width="24" height="24" class="mx-auto" :title="errorMessageBC(product.bc_error)" />
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.id }}
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    <span v-if="product.is_master_product">{{ product.id }}</span>
                                    <span v-else>{{ product.related_master_product_id }}</span>
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.title }}
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.description }}
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.category.title }}
                                </Link>
                            </td>
                            <td>
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.size }}
                                </Link>
                            </td>
                            <td class="text-center">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.hot_buy == 1 ? 'Yes' : 'No' }}
                                </Link>
                            </td>
                            <td class="text-center">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.local == 1 ? 'Yes' : 'No' }}
                                </Link>
                            </td>
                            <td class="text-center">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.special_order == 1 ? 'Yes' : 'No'}}
                                </Link>
                            </td>
                            <td class="text-center">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.retail_special == 1 ? 'Yes' : 'No'}}
                                </Link>
                            </td>
                            <td class="text-center">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.enabled== 1 ? 'Yes' : 'No' }}
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    <span v-if="product.stock === null">&infin;</span>
                                    <span v-else>{{ product.stock }}</span>
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.allocated_wholesale }}
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ product.allocated_retail }}
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    <span v-if="product.stock === null || product.allow_oversell">&infin;</span>
                                    <span v-else>{{ product.available }}</span>
                                </Link>
                            </td>
                            <td class="text-right">
                                <Link class="block px-2 py-1" :href="route('products.edit', product.id)">
                                    {{ toCurrency(Number(product.price)) }}
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <pagination class="inline-flex" :links="products.links" />
        </div>
    </app-layout>
</template>

<script>
import { Link, useForm } from '@inertiajs/inertia-vue3';

import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetLabel from '@/Jetstream/Label';
import Pagination from '@/Jetstream/Pagination';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';

import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const form = useForm('search_data', {
            search: props.filters.search,
            category_id: props.filters.category_id,
            is_wholesale: props.filters.is_wholesale === 'true' ? true : false,
            hot_buy: props.filters.hot_buy === 'true' ? true : false,
            local: props.filters.local === 'true' ? true : false,
            special_order: props.filters.special_order === 'true' ? true : false,
            retail_special: props.filters.retail_special === 'true' ? true : false,
            enabled: props.filters.enabled,
            load_type: props.filters.load_type,
        });

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetInput,
        JetLabel,
        JetTertiaryButton,
        Link,
        Multiselect,
        Pagination,
        Toggle,

    },

    props: ['products', 'categories', 'filters'],

    data() {
        return {
            loadTypeOptions: [
                { value: 'cache', label: 'Cached Items' },
                { value: 'live', label: 'Live Items' },
            ],
            enabledOptions: [
                { value: 1, label: 'Active Items' },
                { value: 0, label: 'Inactive Items' },
            ],
        }
    },
    methods: {
        handleSubmit: _.debounce(function () {
            this.form.get(route('products.index'), { preserveState: true });
        }, 250),

        handleReset () {
            this.form.search = '';
            this.form.category_id = null;
            this.form.is_wholesale = false;
            this.form.hot_buy = false;
            this.form.local = false;
            this.form.special_order = false;
            this.form.retail_special = false;
            this.form.enabled = 1;
            this.form.load_type = 'cache';
            this.handleSubmit();
        },

        BCInfoFilled (product) {
            if (product.bc_error)
                return 'error';
            else if ((product.is_master_product && product.unit_of_measure_id && product.bc_id !== null)
                || (!product.is_master_product && product.unit_of_measure_id && product.related_master_product_id && product.conversion_factor && product.bc_id !== null))
                return 'no_error';
            else return false;
        },
        
        errorMessageBC(errorString) {
            try {
                // Check if the error string is XML
                if (errorString.startsWith('<error')) {
                    // Parse XML string
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(errorString, 'application/xml');
                    const code = xmlDoc.getElementsByTagName('code')[0]?.textContent;
                    const message = xmlDoc.getElementsByTagName('message')[0]?.textContent;
                    if (code && message) {
                        return `${code}. ${message}`;
                    }
                } else {
                    // Parse JSON string
                    const errorObj = JSON.parse(errorString);
                    if (errorObj.error && errorObj.error.message) {
                        return errorObj.error.message;
                    }
                }
            } catch (e) {
                console.error('Error parsing the error string:', e);
            }

            return 'An unknown error occurred';
        },
    },
}
</script>

<style scoped>
.multi-custom {
    min-height: 1.5rem;
}
</style>
